import { Grid, GridDirection, Theme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { ReactNode } from 'react';
import {
    WbControlError,
    WbControlHint,
    WbIcon,
    WbInput,
    WbInputAction,
    WbLink,
    WbSkeleton,
    WbText,
} from '@workbench/react';
import { IconName, WbInputCustomEvent } from '@workbench/core';
import { useTranslation } from '../../i18n/useTranslation';
import Heading from '../dataDisplay/Heading';
import { Direction } from '../../../utils/enums';
import { validationUtils } from '../../../utils/validationFunctions';
import MailToButton from '../../../components/MailToButton';
import { INPUT_MAX_LENGTH_DEFAULT } from '../../../utils/inputMaxLengthConstants';

const useStyles = makeStyles()((theme: Theme) => ({
    textField: {
        marginBottom: theme.spacing(1.5),
    },
    controlHint: {
        textAlign: 'right',
    },
    horizontal: {
        display: 'flex',
        flexDirection: 'row',
    },
    italic: {
        fontStyle: 'italic',
    },
    fullWidth: {
        width: '100%',
    },
    inputLabel: {
        width: '15%',
        height: '30px',
    },
}));

export interface SetValue {
    property: string | number | symbol;
    setValue: (property: string, value: string) => void;
}

export type TextInputType = 'number' | 'search' | 'text' | 'email' | 'password' | 'tel' | 'url' | 'date';

interface TextFieldProps {
    label?: string;
    subLabel?: string;
    value?: string;
    readonly?: boolean;
    disabled?: boolean;
    actionButton?: ReactNode;
    type?: TextInputType;
    inputMode?: 'search' | 'text' | 'email' | 'tel' | 'url' | 'none' | 'numeric' | 'decimal';
    labelSize?: 'l' | 'm' | 's';
    error?: boolean;
    errorText?: string;
    hint?: boolean;
    hintText?: string;
    hintTextAlignRight?: boolean;
    setValue?: SetValue;
    placeholder?: string;
    onWbBlur?: (event: WbInputCustomEvent<void>) => void;
    onWbInput?: (event: WbInputCustomEvent<{ name: string; value: string }>) => void;
    isTextarea?: boolean;
    name?: string;
    required?: boolean;
    maxlength?: number;
    className?: string;
    disableDefaultClass?: boolean;
    pattern?: string;
    labelDirection?: Direction;
    showValueAsText?: boolean;
    showLabelInsideTextField?: boolean;
    compact?: boolean;
    fullWidth?: boolean;
    autoGrow?: boolean;
    spacing?: number;
    isFetching?: boolean;
    leadingIcon?: IconName;
}

const CustomWbTextField = (props: TextFieldProps) => {
    const { classes } = useStyles();
    const translate = useTranslation();

    // If showLabelInsideTextField is not defined, the input is compact, else there would be an empty space above the text
    const shouldCompact = props.showLabelInsideTextField === undefined || props.showLabelInsideTextField === false;

    // If its compact, it should also be full width automatically
    const isFullWidth = shouldCompact || props.fullWidth;

    const getClassName = () => {
        let classNames = '';

        if (isFullWidth) {
            classNames += ` ${classes.fullWidth}`;
        }

        if (props.className) {
            classNames += ` ${props.className}`;
        }

        if (props.disableDefaultClass === undefined || !props.disableDefaultClass) {
            classNames += ` ${classes.textField}`;
        }

        return classNames.trim();
    };

    const getGridDirection = (): GridDirection => {
        switch (props.labelDirection) {
            case Direction.LEFT: {
                return 'row';
            }
            case Direction.TOP: {
                return 'column';
            }
            default:
                return 'column';
        }
    };

    const getGridClass = () => {
        switch (props.labelDirection) {
            case Direction.LEFT: {
                return classes.horizontal;
            }
            case Direction.TOP: {
                return '';
            }
            default:
                return '';
        }
    };

    const getDisplayGridSize = () => {
        if (props.label) {
            if (props.labelDirection === Direction.LEFT) {
                return 8;
            }
        }

        return 12;
    };

    const getLabelGridSize = () => {
        if (props.label) {
            if (props.labelDirection === Direction.LEFT) {
                return 4;
            }
        }

        return 12;
    };

    const getTextComponent = (value?: string) => {
        if (props.isFetching) {
            return <WbSkeleton className={classes.inputLabel} />;
        }
        if (!value || validationUtils.isStringEmpty(value)) {
            return <WbText style={{ fontStyle: 'italic' }}>{translate('no_data_available')}</WbText>;
        }

        switch (props.type) {
            case 'email':
                return <MailToButton label={value} mailto={value} />;
            case 'url': {
                return (
                    <WbLink variant="inline" href={value} target="_blank" rel="noopener noreferrer">
                        {value}
                    </WbLink>
                );
            }
            default:
                return <WbText>{value}</WbText>;
        }
    };

    const getEmailErrorText = (textToCheck: string) => {
        if (!validationUtils.isEmailValid(textToCheck)) {
            return translate('wrong_email_format');
        }

        return '';
    };

    const getUrlErrorText = (textToCheck: string) => {
        if (!validationUtils.isUrlValid(textToCheck)) {
            return translate('wrong_url_format');
        }

        return '';
    };

    const getPhonenumberErrorText = (textToCheck: string) => {
        if (!validationUtils.isValidPhonenumber(textToCheck)) {
            return translate('wrong_phonenumber_format');
        }

        return '';
    };

    const getTextErrorText = (textToCheck: string) => {
        if (!validationUtils.isValidText(textToCheck)) {
            return translate('wrong_text_format');
        }

        return '';
    };

    const getErrorText = (textToCheck?: string) => {
        if (textToCheck === undefined) {
            return translate('mandatory_field');
        }

        if (props.errorText) {
            return props.errorText;
        }

        if (validationUtils.isStringEmpty(textToCheck)) {
            return translate('mandatory_field');
        }

        switch (props.type) {
            case 'email':
                return getEmailErrorText(textToCheck);
            case 'url':
                return getUrlErrorText(textToCheck);
            case 'tel':
                return getPhonenumberErrorText(textToCheck);
            default: {
                return getTextErrorText(textToCheck);
            }
        }
    };

    return (
        <Grid
            container
            direction={getGridDirection()}
            className={getGridClass()}
            size={{ xs: 12 }}
            spacing={props.spacing ?? 0}
        >
            {props.label &&
                (props.showLabelInsideTextField === undefined || props.showLabelInsideTextField === false) && (
                    <Grid size={{ xs: getLabelGridSize() }}>
                        <Heading size={props.labelSize ?? 'm'} header={props.label} mandatory={props.required} />
                        <WbText>{props.subLabel}</WbText>
                    </Grid>
                )}
            <Grid size={{ xs: getDisplayGridSize() }}>
                {props.showValueAsText ? (
                    <WbText className={props.value ? '' : classes.italic}>{getTextComponent(props.value)}</WbText>
                ) : (
                    <WbInput
                        maxlength={props.maxlength ?? INPUT_MAX_LENGTH_DEFAULT}
                        as={props.isTextarea ? 'textarea' : 'input'}
                        name={props.name}
                        error={props.error}
                        readonly={props.readonly}
                        disabled={props.disabled ? props.disabled : undefined}
                        className={getClassName()}
                        value={props.value}
                        onWbinput={
                            props.onWbInput ??
                            (props.setValue
                                ? (event) =>
                                      props.setValue?.setValue(
                                          (props.setValue?.property as string) ?? '',
                                          event.detail.value
                                      )
                                : undefined)
                        }
                        placeholder={props.placeholder}
                        type={props.type ?? 'text'}
                        inputMode={props.inputMode ?? 'text'}
                        onWbblur={props.onWbBlur}
                        pattern={props.pattern}
                        compact={shouldCompact || props.compact}
                        autoGrow={props.autoGrow}
                    >
                        {props.showLabelInsideTextField && <span slot="label">{props.label}</span>}
                        {props.error && <WbControlError slot="message">{getErrorText(props.value)}</WbControlError>}
                        {props.hint && (
                            <WbControlHint
                                slot="message"
                                className={props.hintTextAlignRight ? classes.controlHint : ''}
                            >
                                {props.hintText}
                            </WbControlHint>
                        )}
                        {props.actionButton && (
                            <WbInputAction slot="actions" class="wb-input-action hydrated">
                                {props.actionButton}
                            </WbInputAction>
                        )}
                        {props.leadingIcon && <WbIcon slot="leading-icon" name={props.leadingIcon} />}
                    </WbInput>
                )}
            </Grid>
        </Grid>
    );
};

export default CustomWbTextField;
